(() => {
  if (window.rrFilterEnhancements) return;
  window.rrFilterEnhancements = true;

  const normalize = (value) => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
  const unwrapQuery = (query) => {
    if (!query.startsWith('(') || !query.endsWith(')')) return query;
    let depth = 0;
    let quote = '';
    for (let index = 0; index < query.length; index += 1) {
      const character = query[index];
      if (character === '\\') { index += 1; continue; }
      if (quote) { if (character === quote) quote = ''; continue; }
      if (character === '"' || character === "'") { quote = character; continue; }
      if (character === '(') depth += 1;
      if (character === ')') depth -= 1;
      if (depth === 0 && index < query.length - 1) return query;
    }
    return depth === 0 && !quote ? query.slice(1, -1) : query;
  };
  const readBikeQuery = (query, options) => {
    const trimmed = query.trim();
    for (const option of options) {
      const clause = `tag:"${option.value}"`;
      if (trimmed === clause) return { query: '', bike: option.value };
      const suffix = ` AND ${clause}`;
      if (trimmed.endsWith(suffix)) return { query: unwrapQuery(trimmed.slice(0, -suffix.length)), bike: option.value };
    }
    return { query, bike: '' };
  };
  const applyNativeBike = (url, parameter, selected, options) => {
    if (!parameter) return;
    const known = new Set(options.map((option) => option.value));
    const remaining = url.searchParams.getAll(parameter).flatMap((value) => value.split(',')).filter((value) => !known.has(value));
    url.searchParams.delete(parameter);
    [...new Set([...remaining, selected].filter(Boolean))].forEach((value) => url.searchParams.append(parameter, value));
  };
  const makeCollectionState = (url, context, options, collectionUrl, allCollectionUrl) => {
    const segment = url.pathname.indexOf('/collections/');
    const prefix = segment > 0 ? url.pathname.slice(0, segment) : '';
    const localPath = (path) => prefix && !path.startsWith(prefix + '/') ? prefix + path : path;
    const collectionPath = localPath(new URL(collectionUrl || allCollectionUrl, url.origin).pathname);
    const handle = collectionPath.split('/').filter(Boolean).pop();
    const native = handle === 'vendors' || handle === 'types';
    const tail = url.pathname.startsWith(collectionPath + '/') ? url.pathname.slice(collectionPath.length + 1) : '';
    const tags = tail.split('+').filter(Boolean).map((tag) => { try { return decodeURIComponent(tag); } catch { return tag; } });
    const knownHandles = new Set(options.map((option) => option.dataset.tagHandle));
    const otherTags = tags.filter((tag) => !knownHandles.has(tag));
    const explicitBike = options.find((option) => tags.includes(option.dataset.tagHandle));
    const implicitBike = context?.models?.find((model) => model.handles.includes(handle));
    const appendTags = (path, values) => {
      const tagStart = path.indexOf('/', path.indexOf('/collections/') + '/collections/'.length);
      const base = tagStart < 0 ? path : path.slice(0, tagStart);
      const existing = tagStart < 0 ? [] : path.slice(tagStart + 1).split('+');
      const unique = [...new Set([...existing, ...values])].filter(Boolean);
      return base + (unique.length ? '/' + unique.map(encodeURIComponent).join('+') : '');
    };
    return {
      native,
      bike: explicitBike?.value || implicitBike?.tag || '',
      pathFor: (bike) => {
        if (native) return collectionPath;
        const option = options.find((item) => item.value === bike);
        if (context) {
          if (!option) return appendTags(localPath(context.base), otherTags);
          if (implicitBike?.tag === bike) return appendTags(collectionPath, otherTags);
          const equivalent = context.models?.find((model) => model.tag === bike);
          if (equivalent) return appendTags(localPath(equivalent.path), otherTags);
          return appendTags(localPath(context.base), [...otherTags, option.dataset.tagHandle]);
        }
        return appendTags(collectionPath, [...otherTags, option?.dataset.tagHandle]);
      }
    };
  };
  const mergeForm = (url, form, data) => {
    const names = new Set(Array.from(form.elements).map((element) => element.name).filter(Boolean));
    names.forEach((name) => url.searchParams.delete(name));
    for (const [name, value] of data) if (typeof value === 'string' && value !== '') url.searchParams.append(name, value);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.hash = '';
    return url;
  };
  const initBikeFilter = (panel, form) => {
    const group = panel.querySelector('[data-catalog-bike]');
    if (!group) return;
    const bike = group.querySelector('[data-filter-bike]');
    const options = Array.from(bike.options).filter((option) => option.value);
    const current = new URL(window.location.href);
    const isSearch = group.hasAttribute('data-bike-search');
    const tagParameter = group.dataset.bikeTagParam;
    if (isSearch && !tagParameter) return;
    let context = null;
    try { context = JSON.parse(group.querySelector('[data-bike-rule-context]').textContent); } catch { return; }
    const searchState = readBikeQuery(current.searchParams.get('q') || '', options);
    const collectionState = isSearch ? null : makeCollectionState(current, context, options, group.dataset.collectionUrl, group.dataset.allCollectionUrl);
    const nativeBike = current.searchParams.getAll(tagParameter).flatMap((value) => value.split(',')).find((value) => options.some((option) => option.value === value));
    const currentBike = nativeBike || (isSearch ? searchState.bike : collectionState.bike);
    bike.value = currentBike;
    group.hidden = false;
    const applyContextParams = (url) => {
      Object.entries(context?.params || {}).forEach(([name, value]) => {
        if (name.startsWith('filter.') && typeof value === 'string') url.searchParams.set(name, value);
      });
      return url;
    };
    const applyBike = (url, selected, query = searchState.query) => {
      applyNativeBike(url, tagParameter, isSearch || collectionState.native ? selected : '', options);
      if (isSearch) url.searchParams.set('q', query || '*');
      else {
        url.pathname = collectionState.pathFor(selected);
        if (collectionState.native) url.searchParams.set('q', current.searchParams.get('q') || '');
      }
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      url.hash = '';
      return applyContextParams(url);
    };
    if (!isSearch && context?.redirect === true) {
      const canonical = applyBike(new URL(current), currentBike);
      if (canonical.href !== current.href) {
        window.location.replace(canonical.href);
        return;
      }
    }
    const clearBike = group.querySelector('[data-filter-bike-clear]');
    clearBike.href = applyBike(new URL(current), '').href;
    clearBike.hidden = !currentBike;
    const catalog = panel.closest('.rr-catalog');
    (catalog || panel).querySelectorAll('[data-filter-clear-all]').forEach((clearAll) => {
      const cleared = applyBike(new URL(current), '');
      [...cleared.searchParams.keys()].filter((name) => name.startsWith('filter.')).forEach((name) => cleared.searchParams.delete(name));
      clearAll.href = applyContextParams(cleared).href;
    });
    panel.querySelectorAll('[data-filter-category]').forEach((link) => {
      const target = new URL(link.href, current.origin);
      if (target.origin !== current.origin) return;
      const collectionPath = target.pathname.match(/^(.*\/collections\/[^/]+)/)?.[1];
      if (!collectionPath) return;
      const destination = makeCollectionState(target, null, options, collectionPath, group.dataset.allCollectionUrl);
      if (destination.native) applyNativeBike(target, tagParameter, currentBike, options);
      else target.pathname = destination.pathFor(currentBike);
      const preview = current.searchParams.get('preview_theme_id');
      if (preview) target.searchParams.set('preview_theme_id', preview);
      target.searchParams.delete('page');
      target.searchParams.delete('section_id');
      link.href = target.href;
    });
    form.addEventListener('submit', (event) => {
      if (event.defaultPrevented) return;
      event.preventDefault();
      const target = mergeForm(new URL(current), form, new FormData(form));
      window.location.assign(applyBike(target, bike.value).href);
    });
    catalog?.querySelectorAll('[data-active-filter]').forEach((link) => {
      if (link.dataset.activeFilter !== tagParameter) return;
      const option = options.find((item) => item.value === link.dataset.filterValue);
      if (option) link.remove();
    });
    const activeFilters = catalog?.querySelector('.rr-active-filters');
    if (activeFilters && currentBike) {
      const chip = document.createElement('a');
      chip.href = clearBike.href;
      chip.dataset.bikeFilterChip = '';
      chip.append(document.createTextNode(bike.selectedOptions[0].textContent));
      const icon = document.createElement('span');
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '×';
      const label = document.createElement('span');
      label.className = 'rr-visually-hidden';
      label.textContent = clearBike.textContent;
      chip.append(icon, label);
      activeFilters.appendChild(chip);
    }
    if (isSearch) {
      const searchForm = catalog?.querySelector('[data-catalog-search-form]');
      const input = searchForm?.querySelector('[data-catalog-query]');
      const count = catalog?.querySelector('[data-catalog-result-count]');
      if (input) input.value = searchState.query;
      if (count && currentBike) count.textContent = count.dataset.resultTemplate.replace('__terms__', searchState.query || bike.selectedOptions[0].textContent);
      searchForm?.addEventListener('submit', (event) => {
        if (event.defaultPrevented) return;
        event.preventDefault();
        const target = mergeForm(new URL(current), form, new FormData(form));
        target.searchParams.set('type', 'product');
        window.location.assign(applyBike(target, bike.value, input.value).href);
      });
    }
  };
  const init = (scope = document) => {
    scope.querySelectorAll('[data-filter-panel]').forEach((panel) => {
      if (panel.dataset.filterReady) return;
      panel.dataset.filterReady = 'true';
      const form = panel.querySelector('[data-filter-form]');
      const groups = Array.from(panel.querySelectorAll('[data-filter-group]'));
      const selectedLabel = panel.dataset.selectedLabel;
      const setCount = (badge, count) => {
        if (!badge) return;
        badge.hidden = count === 0;
        badge.textContent = String(count);
        badge.setAttribute('aria-label', selectedLabel.replace('__count__', String(count)));
      };
      const updateCounts = () => {
        let total = Number(panel.dataset.preservedTagCount || 0);
        groups.forEach((group) => {
          const count = group.dataset.filterType === 'bike'
            ? Number(Boolean(group.querySelector('[data-filter-bike]')?.value))
            : group.dataset.filterType === 'price_range'
            ? Number(Array.from(group.querySelectorAll('[data-filter-price]')).some((input) => input.value.trim() !== ''))
            : group.querySelectorAll('input[type="checkbox"]:checked').length;
          total += count;
          setCount(group.querySelector('[data-filter-count]'), count);
        });
        setCount(panel.querySelector('[data-filter-total-count]'), total);
      };
      groups.forEach((group) => {
        const search = group.querySelector('[data-filter-search]');
        if (!search) return;
        const options = Array.from(group.querySelectorAll('[data-filter-option]'));
        const noOptions = group.querySelector('[data-filter-no-options]');
        const labels = options.map((option) => normalize(option.dataset.filterLabel));
        group.querySelector('[data-filter-search-wrap]').hidden = false;
        const filterOptions = () => {
          const terms = normalize(search.value).split(/\s+/).filter(Boolean);
          let visible = 0;
          options.forEach((option, index) => {
            const match = terms.every((term) => labels[index].includes(term));
            option.hidden = !match;
            if (match) visible += 1;
          });
          noOptions.hidden = visible > 0;
        };
        search.addEventListener('input', filterOptions);
        search.addEventListener('search', filterOptions);
        search.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') event.preventDefault();
        });
      });
      form.addEventListener('change', updateCounts);
      form.addEventListener('input', (event) => {
        if (event.target.matches('[data-filter-price]')) updateCounts();
      });
      form.addEventListener('formdata', (event) => {
        form.querySelectorAll('[data-filter-price]').forEach((input) => {
          if (!input.value.trim()) event.formData.delete(input.name);
        });
      });
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'preview_theme_id';
        input.value = preview;
        form.appendChild(input);
        panel.querySelectorAll('[data-filter-clear]').forEach((link) => {
          const url = new URL(link.href, window.location.href);
          url.searchParams.set('preview_theme_id', preview);
          link.href = url.pathname + url.search;
        });
      }
      initBikeFilter(panel, form);
      updateCounts();
      window.addEventListener('pageshow', () => {
        if (panel.isConnected) updateCounts();
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
